import { useState, useEffect, useCallback } from 'react';
import { getProducts } from '../../reducers/products/resources';
import {
    getAccessSummary, createCompanyLicense, updateCompanyLicense, deleteCompanyLicense,
    getUserAccess, setUserAccess,
} from '../access-control/api';
import { defaultBundlesForPlan, FEATURE_KEYS, isGhAdminUser } from '../access-control/featureAccess';

// Single source of truth for a company's licenses + per-user access on the
// /companies page. Wired to the real backend (v3 access-control endpoints).
// apiCore returns the response body; the v2 wrapper nests the handler payload
// under `.data` (same pattern as useUsers).
const mapLicense = (l) => ({
    id: l.id,
    name: l.name,
    planRef: l.plan_ref,
    seatCount: l.seat_count,
    bundles: l.bundles || [],
    seatsUsed: l.seats_used || 0,
});

export default function useCompanyAccess({ companyId }) {
    const [catalog, setCatalog] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [summaryByUser, setSummaryByUser] = useState({}); // user_id -> { bundleKeys, effective }
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getProducts().then((res) => setCatalog(res.data || [])).catch(() => setCatalog([]));
    }, []);

    // One call returns licenses (with seat usage) + per-user bundles/effective.
    const load = useCallback(async () => {
        if (!companyId) { setLicenses([]); setSummaryByUser({}); return; }
        setLoading(true);
        try {
            const res = await getAccessSummary(companyId);
            const payload = (res && res.data) || {};
            setLicenses((payload.licenses || []).map(mapLicense));
            const map = {};
            (payload.users || []).forEach((u) => {
                map[u.user_id] = { bundleKeys: u.bundle_keys || [], effective: u.effective || [] };
            });
            setSummaryByUser(map);
        } catch {
            setLicenses([]); setSummaryByUser({});
        } finally {
            setLoading(false);
        }
    }, [companyId]);
    useEffect(() => { load(); }, [load]);

    // ── Licenses ──
    const addFromPlan = useCallback(async (planId) => {
        const plan = catalog.find((p) => p.id === planId);
        if (!plan || !companyId) return;
        await createCompanyLicense(companyId, {
            name: plan.name, plan_ref: plan.id, seat_count: 1, bundles: defaultBundlesForPlan(plan),
        });
        load();
    }, [catalog, companyId, load]);
    const addCustom = useCallback(async () => {
        if (!companyId) return;
        await createCompanyLicense(companyId, { name: 'New license', plan_ref: null, seat_count: 1, bundles: [] });
        load();
    }, [companyId, load]);
    const update = useCallback(async (licenseId, patch) => {
        if (!companyId) return;
        const body = {};
        if (patch.name !== undefined) body.name = patch.name;
        if (patch.seatCount !== undefined) body.seat_count = patch.seatCount;
        if (patch.bundles !== undefined) body.bundles = patch.bundles;
        await updateCompanyLicense(companyId, licenseId, body);
        load();
    }, [companyId, load]);
    const remove = useCallback(async (licenseId) => {
        if (!companyId) return;
        await deleteCompanyLicense(companyId, licenseId);
        load();
    }, [companyId, load]);

    const usedFor = (licenseId) => {
        const l = licenses.find((x) => x.id === licenseId);
        return l ? l.seatsUsed : 0;
    };
    const overCapacity = licenses.some((l) => l.seatsUsed > l.seatCount);

    // ── Per-user access ──
    const effectiveForUser = useCallback((user) => {
        if (isGhAdminUser(user)) return FEATURE_KEYS;
        return (summaryByUser[user.id] && summaryByUser[user.id].effective) || [];
    }, [summaryByUser]);
    const bundlesForUser = useCallback((user) => {
        if (isGhAdminUser(user)) return [];
        return (summaryByUser[user.id] && summaryByUser[user.id].bundleKeys) || [];
    }, [summaryByUser]);

    // For the Access editor modal (fetches fresh per-user detail on open).
    const loadUserAccess = useCallback(async (userId) => {
        const res = await getUserAccess(companyId, userId);
        const payload = (res && res.data) || {};
        const assignments = (payload.assignments || []).map((a) => ({
            licenseId: a.license_id, bundleKeys: a.bundle_keys || [],
        }));
        const overrides = payload.overrides || { allow: [], deny: [] };
        return { assignments, overrides, hasConnectedCalendar: !!payload.has_connected_calendar };
    }, [companyId]);

    const saveUserAccess = useCallback(async (userId, { assignments, allow, deny }) => {
        const body = {
            assignments: (assignments || []).map((a) => ({ license_id: a.licenseId, bundle_keys: a.bundleKeys })),
            allow: allow || [],
            deny: deny || [],
        };
        const res = await setUserAccess(companyId, userId, body);
        load(); // refresh columns + seat usage
        return (res && res.data) || {}; // { effective, calendarRemoved }
    }, [companyId, load]);

    return {
        catalog, licenses, loading,
        addFromPlan, addCustom, update, remove, usedFor, overCapacity,
        effectiveForUser, bundlesForUser, loadUserAccess, saveUserAccess, refetch: load,
    };
}
